import React, { useState, useEffect } from 'react';

const BulkSend = () => {
    const [connectedDevices, setConnectedDevices] = useState([]);
    const [selectedDevice, setSelectedDevice] = useState('');
    const [numbersInput, setNumbersInput] = useState('');
    const [message, setMessage] = useState('');
    
    // Campaign Progress State
    const [isSending, setIsSending] = useState(false);
    const [progress, setProgress] = useState({ total: 0, sent: 0, failed: 0 });
    const [logs, setLogs] = useState([]);

    // Fetch only CONNECTED devices on page load
    useEffect(() => {
        const fetchDevices = async () => {
            try {
                const response = await fetch('https://whatsapp.hlgroups.in/api/devices');
                const data = await response.json();
                // Filter out devices that are waiting for QR or disconnected
                const activeDevices = data.filter(d => d.status === 'CONNECTED');
                setConnectedDevices(activeDevices);
                if (activeDevices.length > 0) {
                    setSelectedDevice(activeDevices[0].id); // Auto-select the first one
                }
            } catch (error) {
                console.error("Error fetching devices:", error);
            }
        };
        fetchDevices();
    }, []);

    // Helper function to create a delay (prevents WhatsApp bans!)
    const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    const startBulkCampaign = async () => {
        // Parse numbers: split by comma or newline, trim spaces, remove empty lines
        const rawNumbers = numbersInput.split(/[\n,]+/).map(n => n.trim()).filter(n => n !== '');

        if (!selectedDevice) return alert("Please select a connected device.");
        if (rawNumbers.length === 0) return alert("Please enter at least one valid phone number.");
        if (!message.trim()) return alert("Please enter a message to send.");

        // Initialize progress UI
        setIsSending(true);
        setProgress({ total: rawNumbers.length, sent: 0, failed: 0 });
        setLogs([`🚀 Starting bulk campaign for ${rawNumbers.length} numbers...`]);

        // Loop through each number and send via your existing backend API
        for (let i = 0; i < rawNumbers.length; i++) {
            const currentNumber = rawNumbers[i];

            try {
                const response = await fetch('https://whatsapp.hlgroups.in/api/send', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'x-api-key': 'my_secret_key_123' // Your existing secure key
                    },
                    body: JSON.stringify({
                        deviceId: selectedDevice,
                        number: currentNumber,
                        message: message
                    })
                });

                const result = await response.json();

                if (result.success) {
                    setProgress(prev => ({ ...prev, sent: prev.sent + 1 }));
                    setLogs(prev => [`✅ [${currentNumber}] Message delivered.`, ...prev]);
                } else {
                    setProgress(prev => ({ ...prev, failed: prev.failed + 1 }));
                    setLogs(prev => [`❌ [${currentNumber}] Failed: ${result.error || 'Unknown error'}`, ...prev]);
                }
            } catch (error) {
                setProgress(prev => ({ ...prev, failed: prev.failed + 1 }));
                setLogs(prev => [`❌ [${currentNumber}] Server connection error.`, ...prev]);
            }

            // Apply a 3-second safety delay between messages (skip delay on the very last message)
            if (i < rawNumbers.length - 1) {
                setLogs(prev => [`⏳ Waiting 3 seconds for safety...`, ...prev]);
                await sleep(3000); 
            }
        }

        setIsSending(false);
        setLogs(prev => [`🎉 Campaign Complete! Sent: ${progress.sent + 1}, Failed: ${progress.failed}`, ...prev]);
        alert("Bulk messaging campaign has finished!");
    };

    return (
        <div className="bulk-send-page">
            <style>{`
                .card-custom {
                    background: white;
                    border-radius: 8px;
                    box-shadow: 0 4px 6px rgba(0,0,0,0.05);
                    padding: 25px;
                    border-top: 4px solid #007bff;
                }
                .log-box {
                    background-color: #1e1e1e;
                    color: #00ff00;
                    height: 350px;
                    overflow-y: auto;
                    padding: 15px;
                    border-radius: 6px;
                    font-family: monospace;
                    font-size: 14px;
                }
                .log-box p { margin-bottom: 5px; }
                .progress-stats {
                    display: flex;
                    justify-content: space-between;
                    background: #f8f9fa;
                    padding: 15px;
                    border-radius: 6px;
                    margin-bottom: 20px;
                    font-weight: bold;
                }
                textarea { resize: none; }
            `}</style>

            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>📢 Bulk Message Sender</h2>
            </div>

            <div className="row">
                {/* Configuration Column */}
                <div className="col-md-6 mb-4">
                    <div className="card-custom">
                        <h4 className="mb-4">Campaign Setup</h4>
                        
                        <div className="form-group mb-3">
                            <label className="fw-bold">1. Select Sending Device</label>
                            <select 
                                className="form-control mt-1" 
                                value={selectedDevice} 
                                onChange={(e) => setSelectedDevice(e.target.value)}
                                disabled={isSending}
                            >
                                <option value="">-- Choose a Connected Device --</option>
                                {connectedDevices.map(device => (
                                    <option key={device.id} value={device.id}>
                                        {device.name} (Ready)
                                    </option>
                                ))}
                            </select>
                            {connectedDevices.length === 0 && (
                                <small className="text-danger mt-1 d-block">No devices connected. Please connect a device on the Dashboard first.</small>
                            )}
                        </div>

                        <div className="form-group mb-3">
                            <label className="fw-bold">2. Target Numbers (One per line or comma-separated)</label>
                            <textarea 
                                className="form-control mt-1" 
                                rows="5" 
                                placeholder="919876543210&#10;919999999999&#10;447777777777"
                                value={numbersInput}
                                onChange={(e) => setNumbersInput(e.target.value)}
                                disabled={isSending}
                            ></textarea>
                            <small className="text-muted">Include country code. Do not use + or spaces.</small>
                        </div>

                        <div className="form-group mb-4">
                            <label className="fw-bold">3. Message Content</label>
                            <textarea 
                                className="form-control mt-1" 
                                rows="5" 
                                placeholder="Type your promotional or alert message here..."
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                disabled={isSending}
                            ></textarea>
                        </div>

                        <button 
                            className={`btn w-100 btn-lg ${isSending ? 'btn-secondary' : 'btn-primary'}`} 
                            onClick={startBulkCampaign}
                            disabled={isSending || connectedDevices.length === 0}
                        >
                            {isSending ? (
                                <><span className="spinner-border spinner-border-sm me-2"></span> Sending Campaign...</>
                            ) : (
                                '🚀 Start Sending Bulk Messages'
                            )}
                        </button>
                    </div>
                </div>

                {/* Live Tracking Column */}
                <div className="col-md-6 mb-4">
                    <div className="card-custom" style={{ borderTopColor: '#28a745' }}>
                        <h4 className="mb-4">Live Tracker</h4>

                        <div className="progress-stats">
                            <span className="text-primary">Total: {progress.total}</span>
                            <span className="text-success">Sent: {progress.sent}</span>
                            <span className="text-danger">Failed: {progress.failed}</span>
                        </div>

                        {/* Progress Bar */}
                        <div className="progress mb-4" style={{ height: '20px' }}>
                            <div 
                                className="progress-bar progress-bar-striped progress-bar-animated bg-success" 
                                role="progressbar" 
                                style={{ width: `${progress.total > 0 ? ((progress.sent + progress.failed) / progress.total) * 100 : 0}%` }}
                            ></div>
                        </div>

                        {/* Live Console Output */}
                        <div className="log-box">
                            {logs.length === 0 ? (
                                <span className="text-muted">Waiting to start...</span>
                            ) : (
                                logs.map((log, index) => (
                                    <p key={index}>{log}</p>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BulkSend;